import { SourceInfo, SourceMedium, SourceName } from './types';

/* Acquisition source classification. Uses ONLY the real referrer + UTM params. */

const REFERRER_RULES: Array<{ regex: RegExp; source: SourceName; medium: SourceMedium }> = [
  { regex: /(^|\.)google\./i, source: 'google', medium: 'organic' },
  { regex: /(^|\.)bing\./i, source: 'organic_search', medium: 'organic' },
  { regex: /(^|\.)duckduckgo\./i, source: 'organic_search', medium: 'organic' },
  { regex: /(^|\.)yahoo\./i, source: 'organic_search', medium: 'organic' },
  { regex: /(^|\.)yandex\./i, source: 'organic_search', medium: 'organic' },
  { regex: /(^|\.)baidu\./i, source: 'organic_search', medium: 'organic' },
  { regex: /(^|\.)facebook\./i, source: 'facebook', medium: 'social' },
  { regex: /(^|\.)fb\.me|(^|\.)messenger\./i, source: 'facebook', medium: 'social' },
  { regex: /(^|\.)instagram\./i, source: 'instagram', medium: 'social' },
  { regex: /(^|\.)tiktok\./i, source: 'tiktok', medium: 'social' },
  { regex: /(^|\.)linkedin\./i, source: 'linkedin', medium: 'social' },
  { regex: /(^|\.)reddit\./i, source: 'reddit', medium: 'social' },
  { regex: /(^|\.)whatsapp\./i, source: 'whatsapp', medium: 'social' },
  { regex: /wa\.me/i, source: 'whatsapp', medium: 'social' },
  { regex: /t\.me/i, source: 'telegram', medium: 'social' },
  { regex: /(^|\.)telegram\./i, source: 'telegram', medium: 'social' },
];

export function classifyReferrer(referrer: string): { source: SourceName; medium: SourceMedium } {
  if (!referrer) return { source: 'direct', medium: 'direct' };
  let host = '';
  try {
    host = new URL(referrer).hostname;
  } catch {
    host = referrer;
  }
  for (const rule of REFERRER_RULES) {
    if (rule.regex.test(host)) return { source: rule.source, medium: rule.medium };
  }
  return { source: 'other_referral', medium: 'referral' };
}

function readUtm(): { source: string | null; medium: string | null; campaign: string | null } {
  if (typeof window === 'undefined') return { source: null, medium: null, campaign: null };
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('utm_source'),
    medium: params.get('utm_medium'),
    campaign: params.get('utm_campaign'),
  };
}

function normalizeUtmSource(raw: string | null): SourceName | null {
  if (!raw) return null;
  const key = raw.toLowerCase();
  if (/(google|search)/.test(key)) return 'google';
  if (key.includes('fb') || key.includes('facebook')) return 'facebook';
  if (key.includes('insta')) return 'instagram';
  if (key.includes('tiktok')) return 'tiktok';
  if (key.includes('linkedin')) return 'linkedin';
  if (key.includes('reddit')) return 'reddit';
  if (key.includes('whatsapp') || key === 'wa') return 'whatsapp';
  if (key.includes('telegram') || key === 'tg') return 'telegram';
  return 'custom';
}

const UTM_KEY = 'sw_utm';

export function getSourceInfo(): SourceInfo {
  if (typeof window === 'undefined') {
    return { source: 'direct', medium: 'direct', campaign: null, referrer: '' };
  }

  // Capture UTM params once per browser session (persisted in sessionStorage).
  let stored: SourceInfo | null = null;
  try {
    const raw = sessionStorage.getItem(UTM_KEY);
    if (raw) stored = JSON.parse(raw) as SourceInfo;
  } catch {
    stored = null;
  }

  if (!stored) {
    const utm = readUtm();
    const classified = classifyReferrer(document.referrer);
    stored = {
      source: normalizeUtmSource(utm.source) ?? classified.source,
      medium: utm.medium || classified.medium,
      campaign: utm.campaign,
      referrer: document.referrer || '',
    };
    try {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(stored));
    } catch {
      /* sessionStorage unavailable — attribution still works for this page view */
    }
  }

  return stored;
}

export const SOURCE_LABELS: Record<string, string> = {
  direct: 'Direct',
  google: 'Google',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  reddit: 'Reddit',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  organic_search: 'Organic Search',
  other_referral: 'Other Referral',
  custom: 'Campaign',
};